'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Script, SEED_SCRIPTS } from '@/lib/scriptSeed';

// Module-level cache so re-mounts show scripts instantly (survives client nav).
let scriptsMemoryCache: Script[] | null = null;


// ─── Icons ───────────────────────────────────────────────────────────────────
const DiscordIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 640 512"
    width="18"
    height="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M524.531,69.836a1.5,1.5,0,0,0-.764-.7A485.065,485.065,0,0,0,404.081,32.03a1.816,1.816,0,0,0-1.923.91,337.461,337.461,0,0,0-14.9,30.6,447.848,447.848,0,0,0-134.426,0,309.541,309.541,0,0,0-15.135-30.6,1.89,1.89,0,0,0-1.924-.91A483.689,483.689,0,0,0,116.085,69.137a1.712,1.712,0,0,0-.788.676C39.068,183.651,18.186,294.69,28.43,404.354a2.016,2.016,0,0,0,.765,1.375A487.666,487.666,0,0,0,176.02,479.918a1.9,1.9,0,0,0,2.063-.676A348.2,348.2,0,0,0,208.12,430.4a1.86,1.86,0,0,0-1.019-2.588,321.173,321.173,0,0,1-45.868-21.853,1.885,1.885,0,0,1-.185-3.126c3.082-2.309,6.166-4.711,9.109-7.137a1.819,1.819,0,0,1,1.9-.256c96.229,43.917,200.41,43.917,295.5,0a1.812,1.812,0,0,1,1.924.233c2.944,2.426,6.027,4.851,9.132,7.16a1.884,1.884,0,0,1-.162,3.126,301.407,301.407,0,0,1-45.89,21.83,1.875,1.875,0,0,0-1,2.611,391.055,391.055,0,0,0,30.014,48.815,1.864,1.864,0,0,0,2.063.7A486.048,486.048,0,0,0,610.7,405.729a1.882,1.882,0,0,0,.765-1.352C623.729,277.594,590.933,167.465,524.531,69.836ZM222.491,337.58c-28.972,0-52.844-26.587-52.844-59.239S193.056,219.1,222.491,219.1c29.665,0,53.306,26.82,52.843,59.239C275.334,310.993,251.924,337.58,222.491,337.58Zm195.38,0c-28.971,0-52.843-26.587-52.843-59.239S388.437,219.1,417.871,219.1c29.667,0,53.307,26.82,52.844,59.239C470.715,310.993,447.538,337.58,417.871,337.58Z" />
  </svg>
);

const SearchIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="15"
    height="15"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

const ArrowUpIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 20 20"
    width="16"
    height="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 20 20"
    width="14"
    height="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);

const SwordIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M14.5 10L3 21.5M9 5l10 10-3 3-10-10 1-4 2-1zM14 4l6 6"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const VerifiedIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="13"
    height="13"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const StatusIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z"
    />
  </svg>
);

const EmailIcon = () => (
  <svg
    stroke="currentColor"
    fill="currentColor"
    strokeWidth="0"
    viewBox="0 0 512 512"
    width="14"
    height="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M502.3 190.8c3.9-3.1 9.7-.2 9.7 4.7V400c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V195.6c0-5 5.7-7.8 9.7-4.7 22.4 17.4 52.1 39.5 154.1 113.6 21.1 15.4 56.7 47.8 92.2 47.6 35.7.3 72-32.8 92.3-47.6 102-74.1 131.6-96.3 154-113.7zM256 320c23.2.4 56.6-29.2 73.4-41.4 132.7-96.3 142.8-104.7 173.4-128.7 5.8-4.5 9.2-11.5 9.2-18.9v-19c0-26.5-21.5-48-48-48H48C21.5 64 0 85.5 0 112v19c0 7.4 3.4 14.3 9.2 18.9 30.6 23.9 40.7 32.4 173.4 128.7 16.8 12.2 50.2 41.8 73.4 41.4z" />
  </svg>
);

const WebsiteIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="15"
    height="15"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
    />
  </svg>
);

// ─── Rank color helper ────────────────────────────────────────────────────────
function getRankClass(rank: number): string {
  if (rank === 1) return 'rank-1';
  if (rank === 2) return 'rank-2';
  if (rank === 3) return 'rank-3';
  if (rank === 4) return 'rank-4';
  if (rank === 5) return 'rank-5';
  return 'rank-6';
}

function getAnticurveColor(rating: number): string {
  if (rating >= 90) return '#3bea57';
  if (rating >= 75) return '#60a5fa';
  if (rating >= 60) return '#f59e0b';
  return '#ec3b47';
}

interface UIComment {
  _id: string;
  scriptId: number;
  author: {
    _id: string;
    discordId: string;
    username: string;
    avatar: string;
  };
  content: string;
  rating?: number;
  parentId: string | null;
  replyTo?: string | null;
  upvotes: string[];
  downvotes: string[];
  createdAt: string;
  pending?: boolean;
}

interface ReviewsSectionProps {
  scriptId: number;
  scriptName: string;
  currentUser: any;
  onLoginClick: () => void;
}

const StarIcon = ({ filled }: { filled: boolean }) => (
  <svg
    stroke="currentColor"
    fill={filled ? 'currentColor' : 'none'}
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="14"
    height="14"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

const VoteUpIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
    width="12"
    height="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
  </svg>
);

const VoteDownIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2.5"
    viewBox="0 0 24 24"
    width="12"
    height="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const ReplyArrowIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="11"
    height="11"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17l-5-5 5-5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 12h11a5 5 0 015 5v1" />
  </svg>
);

// Caches fetched comments per script so reopening a card shows them instantly
// (stale-while-revalidate: render cached data, refresh in the background).
const commentsCache = new Map<number, UIComment[]>();

// Because every write rotates the session cookie, two writes firing in parallel
// could race (the 2nd uses the pre-rotation cookie → 401). Serialize all writes
// through one chain so each request waits for the previous cookie to settle.
let writeChain: Promise<unknown> = Promise.resolve();
function enqueueWrite<T>(fn: () => Promise<T>): Promise<T> {
  const run = writeChain.then(fn, fn);
  // Keep the chain alive regardless of individual success/failure.
  writeChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

function ReviewsSection({ scriptId, scriptName, currentUser, onLoginClick }: ReviewsSectionProps) {
  const [comments, setComments] = useState<UIComment[]>(() => commentsCache.get(scriptId) || []);
  const [loading, setLoading] = useState(() => !commentsCache.has(scriptId));
  const [newCommentText, setNewCommentText] = useState('');
  const [newRating, setNewRating] = useState(0);
  // Tracks the active reply: which thread (reviewId = stored parentId), who is being
  // addressed, and what they're being notified about.
  const [replyTarget, setReplyTarget] = useState<{
    reviewId: string;
    toName: string;
    toId: string;
    toDiscordId: string;
    parentContent: string;
  } | null>(null);
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = async () => {
    try {
      const res = await fetch(`/api/comments?scriptId=${scriptId}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        commentsCache.set(scriptId, data);
        setComments(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [scriptId]);

  // Keep the cache in sync with settled comments so a reopen is instant.
  useEffect(() => {
    if (!comments.some((c) => c.pending)) {
      commentsCache.set(scriptId, comments);
    }
  }, [comments, scriptId]);

  // Builds an optimistic comment so the UI updates instantly instead of waiting on the DB roundtrip.
  const makeOptimistic = (
    content: string,
    opts: { rating?: number; parentId?: string | null; replyTo?: string | null }
  ): UIComment => ({
    _id: `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    scriptId,
    author: {
      _id: currentUser._id,
      discordId: currentUser.discordId,
      username: currentUser.username,
      avatar: currentUser.avatar,
    },
    content,
    rating: opts.rating,
    parentId: opts.parentId ?? null,
    replyTo: opts.replyTo ?? null,
    upvotes: [],
    downvotes: [],
    createdAt: new Date().toISOString(),
    pending: true,
  });

  const postComment = async (
    tempId: string,
    body: Record<string, unknown>
  ) => {
    try {
      const res = await enqueueWrite(() =>
        fetch('/api/comments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
      );
      const comment = await res.json();
      if (res.ok && comment && !comment.error) {
        // Swap the optimistic placeholder for the real saved comment.
        setComments((prev) => prev.map((c) => (c._id === tempId ? comment : c)));
      } else {
        // Roll back the optimistic insert and surface the reason.
        setComments((prev) => prev.filter((c) => c._id !== tempId));
        if (res.status === 401) {
          onLoginClick();
        } else if (comment?.error) {
          alert(comment.error);
        }
      }
    } catch (err) {
      console.error(err);
      setComments((prev) => prev.filter((c) => c._id !== tempId));
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !newCommentText.trim()) return;
    const content = newCommentText.trim();
    const rating = newRating > 0 ? newRating : undefined;
    const optimistic = makeOptimistic(content, { rating });
    setComments((prev) => [...prev, optimistic]);
    setNewCommentText('');
    setNewRating(0);
    await postComment(optimistic._id, {
      scriptId,
      content,
      rating,
    });
  };

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !replyText.trim() || !replyTarget) return;
    const content = replyText.trim();
    const { reviewId, toName, toId } = replyTarget;
    const optimistic = makeOptimistic(content, { parentId: reviewId, replyTo: toName });
    setComments((prev) => [...prev, optimistic]);
    setReplyText('');
    setReplyTarget(null);
    await postComment(optimistic._id, {
      scriptId,
      content,
      parentId: reviewId,
      // The server derives the recipient + quoted message from this comment id.
      replyToCommentId: toId,
      scriptName,
    });
  };

  const handleVote = async (commentId: string, voteType: 'up' | 'down') => {
    if (!currentUser) {
      onLoginClick();
      return;
    }
    // Can't vote on a comment that hasn't been saved to the DB yet.
    if (commentId.startsWith('temp-')) return;

    const id = currentUser.discordId;
    const snapshot = comments;

    // Apply the toggle locally first so the button reacts instantly.
    setComments((prev) =>
      prev.map((c) => {
        if (c._id !== commentId) return c;
        const up = c.upvotes.filter((u) => u !== id);
        const down = c.downvotes.filter((d) => d !== id);
        if (voteType === 'up' && !c.upvotes.includes(id)) up.push(id);
        if (voteType === 'down' && !c.downvotes.includes(id)) down.push(id);
        return { ...c, upvotes: up, downvotes: down };
      })
    );

    try {
      const res = await enqueueWrite(() =>
        fetch('/api/comments/vote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            commentId,
            voteType,
          }),
        })
      );
      const updatedComment = await res.json();
      if (res.ok && updatedComment && !updatedComment.error) {
        setComments((prev) =>
          prev.map((c) => (c._id === commentId ? updatedComment : c))
        );
      } else {
        setComments(snapshot);
        if (res.status === 401) onLoginClick();
      }
    } catch (err) {
      console.error(err);
      setComments(snapshot);
    }
  };

  const parentReviews = comments.filter((c) => c.parentId === null);
  const ratedReviews = parentReviews.filter((c) => c.rating && c.rating > 0);
  const avgRating = ratedReviews.length > 0
    ? (ratedReviews.reduce((sum, r) => sum + (r.rating || 0), 0) / ratedReviews.length).toFixed(1)
    : null;

  return (
    <div className="reviews-section" onClick={(e) => e.stopPropagation()}>
      <div className="reviews-header">
        <h4 className="reviews-title">
          <span>Community Reviews</span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
            ({parentReviews.length} reviews)
          </span>
        </h4>
        {avgRating && (
          <div className="rating-summary">
            <StarIcon filled={true} />
            <span>{avgRating} / 5.0 Rating</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="reviews-skeleton">
          {[0, 1].map((i) => (
            <div key={i} className="comment-card skeleton-card">
              <div className="skeleton-block skeleton-avatar" />
              <div className="skeleton-lines">
                <div className="skeleton-block skeleton-line short" />
                <div className="skeleton-block skeleton-line" />
                <div className="skeleton-block skeleton-line medium" />
              </div>
            </div>
          ))}
        </div>
      ) : parentReviews.length === 0 ? (
        <div className="reviews-empty">No reviews yet — be the first to leave one.</div>
      ) : (
        <div className="reviews-list">
          {parentReviews.map((review) => {
            const replies = comments.filter((c) => c.parentId === review._id);
            const userIdentifier = currentUser?.discordId;
            const hasUpvoted = review.upvotes.includes(userIdentifier);
            const hasDownvoted = review.downvotes.includes(userIdentifier);

            const startReply = (
              toName: string,
              toId: string,
              toDiscordId: string,
              parentContent: string
            ) => {
              if (!currentUser) {
                onLoginClick();
                return;
              }
              if (replyTarget && replyTarget.toId === toId) {
                setReplyTarget(null);
              } else {
                setReplyTarget({ reviewId: review._id, toName, toId, toDiscordId, parentContent });
                setReplyText('');
              }
            };
            const isReplyingHere = replyTarget?.reviewId === review._id;

            return (
              <div key={review._id} className="review-thread">
                <div className="comment-card" style={review.pending ? { opacity: 0.55 } : undefined}>
                  <div className="user-avatar">
                    {review.author?.avatar ? (
                      <img src={review.author.avatar} alt={review.author.username} />
                    ) : (
                      (review.author?.username || 'U')[0].toUpperCase()
                    )}
                  </div>
                  <div className="comment-content-wrap">
                    <div className="comment-meta">
                      <span className="comment-author">{review.author?.username || 'Unknown User'}</span>
                      {review.rating && review.rating > 0 && (
                        <div className="comment-rating">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <StarIcon key={s} filled={s <= (review.rating || 0)} />
                          ))}
                        </div>
                      )}
                      <span className="comment-time">
                        {review.pending
                          ? 'Posting…'
                          : new Date(review.createdAt).toLocaleDateString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                      </span>
                    </div>
                    <p className="comment-body">{review.content}</p>
                    <div className="comment-actions">
                      <button
                        className={`comment-vote-btn up ${hasUpvoted ? 'active' : ''}`}
                        onClick={() => handleVote(review._id, 'up')}
                        title="Upvote"
                      >
                        <VoteUpIcon />
                        <span>{review.upvotes.length}</span>
                      </button>
                      <button
                        className={`comment-vote-btn down ${hasDownvoted ? 'active' : ''}`}
                        onClick={() => handleVote(review._id, 'down')}
                        title="Downvote"
                      >
                        <VoteDownIcon />
                        <span>{review.downvotes.length}</span>
                      </button>
                      <button
                        className={`comment-action-link ${isReplyingHere && replyTarget?.toId === review._id ? 'active' : ''}`}
                        onClick={() =>
                          startReply(
                            review.author?.username || 'Unknown User',
                            review._id,
                            review.author?.discordId || '',
                            review.content
                          )
                        }
                      >
                        Reply
                      </button>
                    </div>
                  </div>
                </div>

                {replies.length > 0 && (
                  <div className="reply-thread">
                    {replies.map((reply) => {
                      const replyHasUpvoted = reply.upvotes.includes(userIdentifier);
                      const replyHasDownvoted = reply.downvotes.includes(userIdentifier);
                      return (
                        <div
                          key={reply._id}
                          className="comment-card reply"
                          style={reply.pending ? { opacity: 0.55 } : undefined}
                        >
                          <div className="user-avatar reply-avatar">
                            {reply.author?.avatar ? (
                              <img src={reply.author.avatar} alt={reply.author.username} />
                            ) : (
                              (reply.author?.username || 'U')[0].toUpperCase()
                            )}
                          </div>
                          <div className="comment-content-wrap">
                            <div className="comment-meta">
                              <span className="comment-author">{reply.author?.username || 'Unknown User'}</span>
                              {reply.replyTo && (
                                <span className="comment-mention">
                                  <ReplyArrowIcon />@{reply.replyTo}
                                </span>
                              )}
                              <span className="comment-time">
                                {reply.pending
                                  ? 'Posting…'
                                  : new Date(reply.createdAt).toLocaleDateString(undefined, {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                              </span>
                            </div>
                            <p className="comment-body">{reply.content}</p>
                            <div className="comment-actions">
                              <button
                                className={`comment-vote-btn up ${replyHasUpvoted ? 'active' : ''}`}
                                onClick={() => handleVote(reply._id, 'up')}
                              >
                                <VoteUpIcon />
                                <span>{reply.upvotes.length}</span>
                              </button>
                              <button
                                className={`comment-vote-btn down ${replyHasDownvoted ? 'active' : ''}`}
                                onClick={() => handleVote(reply._id, 'down')}
                              >
                                <VoteDownIcon />
                                <span>{reply.downvotes.length}</span>
                              </button>
                              <button
                                className={`comment-action-link ${replyTarget?.toId === reply._id ? 'active' : ''}`}
                                onClick={() =>
                                  startReply(
                                    reply.author?.username || 'Unknown User',
                                    reply._id,
                                    reply.author?.discordId || '',
                                    reply.content
                                  )
                                }
                              >
                                Reply
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {isReplyingHere && (
                  <form onSubmit={handleSubmitReply} className="review-form reply-form">
                    <div className="reply-context-bar">
                      <span className="reply-context-text">
                        <ReplyArrowIcon />
                        Replying to <strong>@{replyTarget.toName}</strong>
                      </span>
                      <button
                        type="button"
                        className="reply-context-close"
                        onClick={() => setReplyTarget(null)}
                        title="Cancel reply"
                      >
                        ✕
                      </button>
                    </div>
                    <textarea
                      className="comment-input-area"
                      placeholder={`Reply to @${replyTarget.toName}… (Enter to send, Shift+Enter for new line)`}
                      required
                      autoFocus
                      maxLength={600}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (replyText.trim() && !submitting) {
                            e.currentTarget.form?.requestSubmit();
                          }
                        }
                      }}
                    />
                    <div className="form-submit-row">
                      <button
                        type="button"
                        className="form-btn form-btn-secondary"
                        onClick={() => setReplyTarget(null)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting || !replyText.trim()}
                        className="form-btn form-btn-primary"
                      >
                        Reply
                      </button>
                    </div>
                  </form>
                )}
              </div>
            );
          })}
        </div>
      )}

      {currentUser ? (
        <form onSubmit={handleSubmitComment} className="review-form">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', fontFamily: 'var(--font-poppins)' }}>
              Write a Review
            </span>
            <div className="star-rating-select">
              <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginRight: 4, fontFamily: 'var(--font-poppins)' }}>
                Rating:
              </span>
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  type="button"
                  key={s}
                  className={`star-btn ${s <= newRating ? 'active' : ''}`}
                  onClick={() => setNewRating(s)}
                >
                  <StarIcon filled={s <= newRating} />
                </button>
              ))}
            </div>
          </div>
          <textarea
            className="comment-input-area"
            placeholder="Share your thoughts about this script..."
            required
            maxLength={1000}
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
          />
          <div className="form-submit-row">
            <button
              type="submit"
              disabled={submitting}
              className="form-btn form-btn-primary"
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      ) : (
        <div
          style={{
            background: 'rgba(88,101,242,0.06)',
            border: '1px dashed rgba(88,101,242,0.2)',
            borderRadius: 8,
            padding: '16px 20px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', fontFamily: 'var(--font-poppins)' }}>
            You must be logged in to leave reviews or write replies.
          </span>
          <button
            onClick={onLoginClick}
            className="discord-join-btn"
            style={{
              padding: '6px 16px',
              fontSize: 12,
              background: '#5865f2',
              borderColor: 'rgba(88,101,242,0.3)',
              color: '#fff',
            }}
          >
            <DiscordIcon />
            <span>Login with Discord</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Script Card Component ────────────────────────────────────────────────────
interface ScriptCardProps {
  script: Script;
  style?: React.CSSProperties;
  currentUser: any;
  onLoginClick: () => void;
}

function ScriptCard({ script, style, currentUser, onLoginClick }: ScriptCardProps) {
  const [expanded, setExpanded] = useState(false);
  const isUpdated = script.status === 'Updated';
  const isPending = script.pending || script.category === 'Pending';
  const isDiscontinued = script.status === 'Discontinued';
  const statusClass = isPending
    ? 'pending'
    : isDiscontinued
      ? 'discontinued'
      : isUpdated
        ? 'updated'
        : 'not-updated';
  const toggleExpanded = () => setExpanded((v) => !v);

  return (
    <div
      className="script-card"
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={`${script.name} details`}
      onClick={toggleExpanded}
      onKeyDown={(event) => {
        // Only toggle when the card itself is focused — not when typing inside
        // a nested input/textarea/button (otherwise Space/Enter would collapse it).
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleExpanded();
        }
      }}
      style={{
        animationDelay: `${(Math.max(script.rank, 1) - 1) * 60}ms`,
        ...style,
      }}
    >
      {/* Rank accent border */}
      <div
        className="script-card-accent"
        style={{
          background: isDiscontinued
            ? 'rgba(255, 255, 255, 0.15)'
            : isPending
              ? '#a78bfa'
              : isUpdated
                ? 'var(--weao-green)'
                : 'var(--weao-red)',
        }}
      />

      <div className="script-card-body">
        <div className="script-card-top">
          {/* Rank badge */}
          {!isPending && !isDiscontinued && (
            <span
              className={`rank-badge ${getRankClass(script.rank)}`}
              title={`Rank #${script.rank}`}
            >
              #{script.rank}
            </span>
          )}

          <h3 className="script-name">{script.name}</h3>

          <div className="script-meta">
            {/* Free / Paid / Key System */}
            {script.free && <span className="feature-badge free">Free</span>}
            {isPending && (
              <span className="feature-badge in-review">
                <ShieldIcon />
                Under Review
              </span>
            )}
            {script.private && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontFamily: 'var(--font-poppins)',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: 'rgba(245,158,11,0.12)',
                  color: '#fbbf24',
                  border: '1px solid rgba(245,158,11,0.25)',
                }}
              >
                <ShieldIcon />
                Private
              </span>
            )}
            {script.paid && (
              <span
                style={{
                  fontFamily: 'var(--font-poppins)',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: 'rgba(168,85,247,0.12)',
                  color: '#c084fc',
                  border: '1px solid rgba(168,85,247,0.25)',
                }}
              >
                Paid
              </span>
            )}
            {script.keySystem && (
              <span
                style={{
                  fontFamily: 'var(--font-poppins)',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  background: 'rgba(14,165,233,0.12)',
                  color: '#38bdf8',
                  border: '1px solid rgba(14,165,233,0.25)',
                }}
              >
                Key System
              </span>
            )}
            {script.backwardDetection && (
              <span className="feature-badge backward-detection">
                <VerifiedIcon />
                Backward Detection
              </span>
            )}
          </div>
        </div>

        {!isPending && (
          <>
            <p className="script-updated-time" style={{ marginTop: 4 }}>Last updated: {script.lastUpdated}</p>
          </>
        )}

        {/* Expanded info section */}
        {expanded && (
          <div
            style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            {/* Description */}
            {script.private || isPending ? (
              <div className="script-private-notice">{script.description}</div>
            ) : (
              <p
                style={{
                  fontFamily: 'var(--font-poppins)',
                  fontSize: 13,
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {script.description}
              </p>
            )}

            {script.team && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  padding: '8px 10px',
                  borderRadius: 7,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  fontFamily: 'var(--font-poppins)',
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0,
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.42)',
                  }}
                >
                  Team
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: 'rgba(255,255,255,0.72)',
                    lineHeight: 1.5,
                  }}
                >
                  {script.team}
                </span>
              </div>
            )}

            {/* Links */}
            {(script.website || script.discord) && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {script.website && (
                  <a
                    onClick={(event) => event.stopPropagation()}
                    href={script.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 14px',
                      borderRadius: 7,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#fff',
                      fontFamily: 'var(--font-poppins)',
                      fontSize: 12,
                      fontWeight: 500,
                      textDecoration: 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')
                    }
                  >
                    <WebsiteIcon />
                    Website
                  </a>
                )}
                {script.discord && (
                  <a
                    onClick={(event) => event.stopPropagation()}
                    href={script.discord}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 14px',
                      borderRadius: 7,
                      background: 'rgba(88,101,242,0.15)',
                      border: '1px solid rgba(88,101,242,0.3)',
                      color: '#7289da',
                      fontFamily: 'var(--font-poppins)',
                      fontSize: 12,
                      fontWeight: 500,
                      textDecoration: 'none',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = 'rgba(88,101,242,0.25)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = 'rgba(88,101,242,0.15)')
                    }
                  >
                    <DiscordIcon />
                    Discord
                  </a>
                )}
              </div>
            )}

            {/* Reviews / Comments tab & section */}
            {!isPending && (
              <ReviewsSection
                scriptId={script.id}
                scriptName={script.name}
                currentUser={currentUser}
                onLoginClick={onLoginClick}
              />
            )}
          </div>
        )}
      </div>

      {/* Status badge */}
      <div className="script-status">
        <span className={`status-badge ${statusClass}`}>{script.status}</span>
      </div>
    </div>
  );
}

// ─── Notifications ──────────────────────────────────────────────────────────
interface UINotification {
  _id: string;
  fromUsername: string;
  fromAvatar: string;
  scriptName: string;
  parentContent: string;
  replyContent: string;
  read: boolean;
  createdAt: string;
}

const BellIcon = () => (
  <svg
    stroke="currentColor"
    fill="none"
    strokeWidth="2"
    viewBox="0 0 24 24"
    width="18"
    height="18"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
    />
  </svg>
);

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function NotificationBell({ currentUser }: { currentUser: { discordId: string } }) {
  const [notifications, setNotifications] = useState<UINotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = async () => {
    try {
      const res = await fetch('/api/notifications');
      if (!res.ok) return;
      const data = await res.json();
      if (data && Array.isArray(data.notifications)) {
        setNotifications(data.notifications);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Initial load + poll every 30s while logged in.
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.discordId]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unreadCount > 0) {
      // Optimistically clear the badge, then persist.
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      try {
        await fetch('/api/notifications/read', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        className="notif-bell"
        onClick={toggleOpen}
        title="Notifications"
        aria-label="Notifications"
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown">
          <div className="notif-dropdown-header">Notifications</div>
          {notifications.length === 0 ? (
            <div className="notif-empty">No notifications yet.</div>
          ) : (
            <div className="notif-list">
              {notifications.map((n) => (
                <div key={n._id} className={`notif-item ${n.read ? '' : 'unread'}`}>
                  <div className="user-avatar notif-avatar">
                    {n.fromAvatar ? (
                      <img src={n.fromAvatar} alt={n.fromUsername} />
                    ) : (
                      (n.fromUsername || 'U')[0].toUpperCase()
                    )}
                  </div>
                  <div className="notif-body">
                    <p className="notif-text">
                      <strong>{n.fromUsername}</strong> replied to your message
                      {n.scriptName ? (
                        <>
                          {' '}in <span className="notif-script">{n.scriptName}</span>
                        </>
                      ) : null}
                    </p>
                    {n.parentContent && (
                      <p className="notif-quote">“{n.parentContent}”</p>
                    )}
                    <p className="notif-reply">{n.replyContent}</p>
                    <span className="notif-time">{timeAgo(n.createdAt)}</span>
                  </div>
                  {!n.read && <span className="notif-dot" />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [search, setSearch] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [currentUser, setCurrentUser] = useState<{ _id: string; discordId: string; username: string; avatar: string } | null>(null);
  // Scripts come from the DB (managed via /admin). We hydrate instantly from
  // localStorage/module cache, then revalidate in the background.
  const [scripts, setScripts] = useState<Script[]>(() => {
    if (scriptsMemoryCache) return scriptsMemoryCache;
    if (typeof window !== 'undefined') {
      try {
        const cached = localStorage.getItem('bb_scripts');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            scriptsMemoryCache = parsed;
            return parsed;
          }
        }
      } catch {
        /* ignore */
      }
    }
    return SEED_SCRIPTS;
  });

  useEffect(() => {
    fetch('/api/scripts')
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          scriptsMemoryCache = data;
          setScripts(data);
          try {
            localStorage.setItem('bb_scripts', JSON.stringify(data));
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('bb_user');
    if (saved) {
      try {
        setCurrentUser(JSON.parse(saved));
      } catch (e) {
        console.error(e);
      }
    }
  }, []);

  const handleDiscordLogin = () => {
    // Must match EXACTLY the redirect registered in the Discord Developer Portal
    // and the one used server-side in /api/auth/callback/discord.
    const redirectUri =
      process.env.NEXT_PUBLIC_DISCORD_REDIRECT_URI ||
      'https://bladeballranking.vercel.app/api/auth/callback/discord';
    const clientId = process.env.NEXT_PUBLIC_DISCORD_CLIENT_ID || '1510719764488650924';
    const oauthUrl = `https://discord.com/oauth2/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}&response_type=code&scope=identify`;

    const popup = window.open(oauthUrl, 'Discord Login', 'width=500,height=750');

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'DISCORD_AUTH_SUCCESS') {
        const user = event.data.user;
        setCurrentUser(user);
        localStorage.setItem('bb_user', JSON.stringify(user));
        window.removeEventListener('message', handleMessage);
      }
    };

    window.addEventListener('message', handleMessage);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredScripts = scripts.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );
  const rankedScripts = filteredScripts
    .filter((script) => script.category === 'Blade Ball')
    .sort((a, b) => a.rank - b.rank);

  // Group rankedScripts by their rank to render same-rank scripts side-by-side
  const groupedRankedScripts: Script[][] = [];
  rankedScripts.forEach((script) => {
    const lastGroup = groupedRankedScripts[groupedRankedScripts.length - 1];
    if (lastGroup && lastGroup[0].rank === script.rank) {
      lastGroup.push(script);
    } else {
      groupedRankedScripts.push([script]);
    }
  });

  const pendingScripts = filteredScripts.filter((script) => script.category === 'Pending');
  const discontinuedScripts = filteredScripts.filter((script) => script.category === 'Discontinued');

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header className="topbar">
        {/* Logo */}
        <Link href="/" className="logo">
          <span>bladeballranking</span>
          <span className="logo-suffix">
            <span className="red">.</span>
            <span>vercel</span>
            <span className="green">.app</span>
          </span>
        </Link>

        {/* Nav center */}
        <nav className="nav-center">
          <a href="#scripts" className="nav-link">
            <SwordIcon />
            <span>Scripts</span>
          </a>
          <Link href="/forum" className="nav-link">
            <span>Forum</span>
          </Link>
          <a
            href="https://discord.gg/eVMZMW2SfV"
            className="nav-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <DiscordIcon />
            <span>Discord</span>
          </a>
        </nav>

        {/* Right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 60 }}>
          {currentUser ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NotificationBell currentUser={currentUser} />
              <div className="user-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>
                {currentUser.avatar ? (
                  <img src={currentUser.avatar} alt={currentUser.username} />
                ) : (
                  currentUser.username[0].toUpperCase()
                )}
              </div>
              <span style={{ fontFamily: 'var(--font-poppins)', fontSize: 13, color: '#fff', fontWeight: 600 }}>
                {currentUser.username}
              </span>
              <button
                onClick={() => {
                  localStorage.removeItem('bb_user');
                  setCurrentUser(null);
                  fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
                }}
                className="comment-action-link"
                style={{ padding: '4px 8px', fontSize: 11 }}
              >
                Logout
              </button>
            </div>
          ) : (
            <button
              onClick={handleDiscordLogin}
              className="themes-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <DiscordIcon />
              Login with Discord
            </button>
          )}
        </div>
      </header>

      {/* ── Main ───────────────────────────────────────────────────────── */}
      <main className="main-content">
        {/* ── Discord Banner ─────────────────────────────────────────── */}
        <div className="card discord-banner">
          <div className="discord-icon-wrap">
            <DiscordIcon />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 16,
                fontWeight: 400,
                color: '#fff',
                lineHeight: 1.2,
              }}
            >
              Join the Blade Ball Scripts Community!
            </div>
            <div
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 14,
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.2,
              }}
            >
              Get live script status updates, support, and update pings from the bot!
            </div>
          </div>
          <a
            href="https://discord.gg/eVMZMW2SfV"
            target="_blank"
            rel="noopener noreferrer"
            className="discord-join-btn"
          >
            <span>Join Discord</span>
            <ArrowRightIcon />
          </a>
        </div>

        {/* ── Scripts Section ────────────────────────────────────────── */}
        <div id="scripts" style={{ marginTop: 8 }}>
          {/* Section header */}
          <div className="section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 className="section-title">Blade Ball Scripts</h2>
              <span
                style={{
                  fontFamily: 'var(--font-poppins)',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '2px 10px',
                  borderRadius: 20,
                  background: 'rgba(59,234,87,0.12)',
                  color: 'var(--weao-green)',
                  border: '1px solid rgba(59,234,87,0.25)',
                }}
              >
                Ranked
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Search */}
              <div className="search-wrap">
                <span className="search-icon">
                  <SearchIcon />
                </span>
                <input
                  type="text"
                  placeholder="Search scripts..."
                  className="search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Ranking legend */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 12,
                color: 'rgba(255,255,255,0.4)',
              }}
            >
              Ranked by Anti-Curve performance &amp; overall Blade Ball ability:
            </span>
            {[1, 2, 3, 4, 5].map((r) => (
              <span key={r} className={`rank-badge ${getRankClass(r)}`}>
                #{r}
              </span>
            ))}
          </div>

          {/* Script list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {groupedRankedScripts.length > 0 ? (
              groupedRankedScripts.map((group) => {
                if (group.length === 1) {
                  return <ScriptCard key={group[0].id} script={group[0]} currentUser={currentUser} onLoginClick={handleDiscordLogin} />;
                }
                return (
                  <div
                    key={`rank-group-${group[0].rank}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: '12px',
                      marginBottom: '8px',
                    }}
                  >
                    {group.map((script) => (
                      <ScriptCard key={script.id} script={script} style={{ marginBottom: 0 }} currentUser={currentUser} onLoginClick={handleDiscordLogin} />
                    ))}
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'rgba(255,255,255,0.35)',
                  fontFamily: 'var(--font-poppins)',
                  fontSize: 14,
                }}
              >
                No scripts found matching &ldquo;{search}&rdquo;
              </div>
            )}
          </div>
        </div>

        {/* Pending Section */}
        <div id="pending" style={{ marginTop: 12 }}>
          <div className="section-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <h2 className="section-title">Pending</h2>
              <span
                style={{
                  fontFamily: 'var(--font-poppins)',
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '2px 10px',
                  borderRadius: 20,
                  background: 'rgba(167,139,250,0.12)',
                  color: '#c4b5fd',
                  border: '1px solid rgba(167,139,250,0.25)',
                }}
              >
                Awaiting ranking
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {pendingScripts.length > 0 ? (
              pendingScripts.map((script) => <ScriptCard key={script.id} script={script} currentUser={currentUser} onLoginClick={handleDiscordLogin} />)
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '28px 20px',
                  color: 'rgba(255,255,255,0.35)',
                  fontFamily: 'var(--font-poppins)',
                  fontSize: 14,
                }}
              >
                No pending scripts found matching &ldquo;{search}&rdquo;
              </div>
            )}
          </div>
        </div>

        {/* Discontinued Section */}
        {discontinuedScripts.length > 0 && (
          <div id="discontinued" style={{ marginTop: 12 }}>
            <div className="section-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <h2 className="section-title">Discontinued</h2>
                <span
                  style={{
                    fontFamily: 'var(--font-poppins)',
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '2px 10px',
                    borderRadius: 20,
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'rgba(255, 255, 255, 0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                  }}
                >
                  No longer updated
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {discontinuedScripts.map((script) => (
                <ScriptCard key={script.id} script={script} currentUser={currentUser} onLoginClick={handleDiscordLogin} />
              ))}
            </div>
          </div>
        )}

        {/* ── Info Cards ─────────────────────────────────────────────── */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: 12,
            marginTop: 8,
          }}
        >
          {/* Anti-Curve explained */}
          <div className="card">
            <h4
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 15,
                fontWeight: 600,
                color: '#60a5fa',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              <ShieldIcon />
              What is Anti-Curve?
            </h4>
            <p
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 13,
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.6,
              }}
            >
              Anti-Curve is a Blade Ball script feature that tries to read and counter the
              ball&apos;s curved movement. Higher ratings mean it was more consistent in normal use.
            </p>
          </div>

          {/* Ranking method */}
          <div className="card">
            <h4
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 15,
                fontWeight: 600,
                color: '#f59e0b',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              <SwordIcon />
              How We Rank
            </h4>
            <p
              style={{
                fontFamily: 'var(--font-poppins)',
                fontSize: 13,
                color: 'rgba(255,255,255,0.6)',
                lineHeight: 1.6,
              }}
            >
              Scripts are ranked by anti-curve consistency, auto-parry speed, update activity,
              stability, and feedback from Blade Ball players.
            </p>
          </div>
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-top">
            {/* Brand */}
            <div className="footer-brand">
              <Link href="/" className="logo" style={{ fontSize: 20 }}>
                <span>bladeballranking</span>
                <span className="logo-suffix">
                  <span className="red">.</span>
                  <span>vercel</span>
                  <span className="green">.app</span>
                </span>
              </Link>
              <p style={{ marginTop: 4 }}>
                Blade Ball script ranking based on anti-curve and speed
              </p>
            </div>

            {/* Links */}
            <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
              <div className="footer-links-group">
                <h4>Site</h4>
                <a href="#scripts" className="footer-link">
                  <StatusIcon />
                  Scripts
                </a>
              </div>

              <div className="footer-links-group">
                <h4>Connect</h4>
                <a
                  href="https://discord.gg/eVMZMW2SfV"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  <DiscordIcon />
                  Discord
                </a>
                <a href="mailto:contact@bladeballranking.vercel.app" className="footer-link">
                  <EmailIcon />
                  Email
                </a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-copy">
              © 2024-2026 <span>bladeballranking.vercel.app</span>. All rights reserved.
            </div>
            <div className="footer-credit">
              This website is based off{' '}
              <a
                href="https://whatexpsare.online/"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-inline-link"
              >
                whatexpsare.online
              </a>
              , so credits to them and their style.
            </div>
            <a href="#" className="footer-trustpilot">
              Share your experience
            </a>
          </div>
        </div>
      </footer>

      {/* ── Scroll to top ───────────────────────────────────────────────── */}
      {showScrollTop && (
        <button
          className="scroll-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Back to top"
        >
          <ArrowUpIcon />
        </button>
      )}
    </>
  );
}
